/**
 * Error Boundary Middleware for RTK Query
 *
 * This middleware provides error handling and recovery mechanisms for API calls.
 * It catches errors, logs them, and provides fallback responses to prevent app crashes.
 */

import { type AnyAction, type Middleware } from "@reduxjs/toolkit";

import Logger from "@/helper/logger";

export interface ErrorBoundaryConfig {
  /** Enable error logging */
  enableLogging?: boolean;
  /** Enable fallback responses for failed queries */
  enableFallbacks?: boolean;
  /** Custom error handlers by endpoint */
  errorHandlers?: Record<string, (error: unknown) => unknown>;
  /** Default fallback data by endpoint */
  fallbackData?: Record<string, unknown>;
}

export interface ErrorContext {
  endpointName: string;
  args: unknown;
  error: unknown;
  timestamp: number;
}

/**
 * Creates an error boundary middleware for RTK Query
 * @param config - Configuration options for the error boundary
 * @returns The configured middleware function
 */
export const createErrorBoundaryMiddleware = (config: ErrorBoundaryConfig = {}): Middleware => {
  const { enableLogging = true, enableFallbacks = true, errorHandlers = {}, fallbackData = {} } = config;

  return () => (next) => (action) => {
    try {
      handleFulfilledActions(action, enableLogging, enableFallbacks, fallbackData);
      handleRejectedActions(action, enableLogging, errorHandlers);
      return next(action);
    } catch (middlewareError) {
      if (enableLogging) {
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary] Middleware error:", middlewareError);
      }

      // Continue with original action to prevent breaking the store
      return next(action);
    }
  };
};

/**
 * Handle fulfilled actions with payload validation
 * @param action - The Redux action
 * @param enableLogging - Whether logging is enabled
 * @param enableFallbacks - Whether fallbacks are enabled
 * @param fallbackData - Fallback data by endpoint
 */
function handleFulfilledActions(
  action: AnyAction,
  enableLogging: boolean,
  enableFallbacks: boolean,
  fallbackData: Record<string, unknown>
): void {
  if (!action.type?.endsWith("/fulfilled")) return;

  const endpointName = extractEndpointName(action.type);

  if (endpointName && action.payload) {
    try {
      validatePayloadStructure(action.payload);
    } catch (validationError) {
      if (enableLogging) {
        Logger.warn(`[ErrorBoundary] Invalid payload structure for ${endpointName}:`, {
          context: "ErrorBoundary",
          data: {
            error: validationError,
            payload: action.payload,
            action,
          },
        });
      }

      if (enableFallbacks && fallbackData[endpointName]) {
        action.payload = fallbackData[endpointName];
        logFallbackApplication(enableLogging, endpointName);
      }
    }
  }
}

/**
 * Handle rejected actions with error handling
 * @param action - The Redux action
 * @param enableLogging - Whether logging is enabled
 * @param errorHandlers - Custom error handlers by endpoint
 */
function handleRejectedActions(
  action: AnyAction,
  enableLogging: boolean,
  errorHandlers: Record<string, (error: unknown) => unknown>
): void {
  if (!action.type?.endsWith("/rejected")) return;

  const endpointName = extractEndpointName(action.type);
  const errorContext: ErrorContext = {
    endpointName: endpointName || "unknown",
    args: action.meta?.arg,
    error: action.payload || action.error,
    timestamp: Date.now(),
  };

  if (enableLogging) {
    Logger.error(`[ErrorBoundary] API Error in ${errorContext.endpointName}:`, {
      context: "ErrorBoundary",
      data: errorContext,
    });
  }

  if (endpointName && errorHandlers[endpointName]) {
    try {
      const handledError = errorHandlers[endpointName](errorContext.error);
      action.payload = handledError;
    } catch (handlerError) {
      if (enableLogging) {
        // eslint-disable-next-line no-console
        console.error(`[ErrorBoundary] Error handler failed for ${endpointName}:`, handlerError);
      }
    }
  }

  storeErrorContext(errorContext);
}

/**
 * Log fallback application
 * @param enableLogging - Whether logging is enabled
 * @param endpointName - The endpoint name
 */
function logFallbackApplication(enableLogging: boolean, endpointName: string): void {
  if (enableLogging) {
    // eslint-disable-next-line no-console
    console.info(`[ErrorBoundary] Applied fallback data for ${endpointName}`);
  }
}

/**
 * Extract endpoint name from RTK Query action type
 * @param actionType - The RTK Query action type string
 * @returns The extracted endpoint name or null
 */
function extractEndpointName(actionType: string): string | null {
  const match = actionType.match(/^(.+)Api\/executeQuery\/(.+)\/(pending|fulfilled|rejected)$/);
  return match ? match[2] : null;
}

/**
 * Basic payload structure validation
 * @param payload - The payload to validate
 * @returns void
 */
function validatePayloadStructure(payload: unknown): void {
  if (payload === null || payload === undefined) {
    throw new Error("Payload is null or undefined");
  }

  if (typeof payload === "object" && Array.isArray(payload)) {
    // Validate array payload
    for (let i = 0; i < payload.length; i++) {
      if (payload[i] === null || payload[i] === undefined) {
        throw new Error(`Array item at index ${i} is null or undefined`);
      }
    }
  }
}

/**
 * Store error context for debugging and monitoring
 */
const errorContextStore: ErrorContext[] = [];
const MAX_ERROR_CONTEXTS = 50;

function storeErrorContext(context: ErrorContext): void {
  errorContextStore.push(context);

  // Keep only the last MAX_ERROR_CONTEXTS errors
  if (errorContextStore.length > MAX_ERROR_CONTEXTS) {
    errorContextStore.shift();
  }
}

/**
 * Get recent error contexts for debugging
 * @returns Array of recent error contexts
 */
export function getRecentErrors(): ErrorContext[] {
  return [...errorContextStore];
}

/**
 * Clear error context store
 */
export function clearErrorContexts(): void {
  errorContextStore.length = 0;
}

/**
 * Get error statistics
 * @returns Object with error counts by endpoint
 */
export function getErrorStats(): Record<string, number> {
  const stats: Record<string, number> = {};

  for (const context of errorContextStore) {
    stats[context.endpointName] = (stats[context.endpointName] || 0) + 1;
  }

  return stats;
}
